import { useContext, useEffect, useRef } from 'react';
import { ShortcutContext } from 'Context/Shortcut';

let scopeCounter = 0;

// Shared id generator, also used directly by class components that can't
// call hooks (see UserCreationModal) - they push/pop via
// context.pushScope/popScope in componentDidMount/componentWillUnmount
// instead of this hook.
export const createScopeId = (label = 'scope') => {
  scopeCounter += 1;
  return `${label}-${scopeCounter}`;
};

// Call this once from the root of an overlay (modal/drawer) that should
// "own" the keyboard while it's visible - e.g. a vulnerability detail
// drawer with its own tabs, on top of the table's own tabs. Pushes a scope
// while `enabled` is true and pops it otherwise (on unmount, or as soon as
// `enabled` flips to false) - covers both overlays that mount/unmount
// entirely and ones that stay mounted and toggle a "visible" flag instead.
// Returns a unique scope id to pass to
// useShortcut/useFormShortcuts/useTabsShortcuts. Stacking two instances of
// the same overlay (e.g. a confirm dialog opened from within a modal) gets
// independent scope ids automatically, so they never collide.
const useShortcutScope = (label = 'scope', enabled = true) => {
  const shortcutContext = useContext(ShortcutContext);
  const scopeIdRef = useRef(null);
  if (!scopeIdRef.current) scopeIdRef.current = createScopeId(label);

  useEffect(() => {
    if (!shortcutContext || !enabled) return undefined;
    const scopeId = scopeIdRef.current;
    shortcutContext.pushScope(scopeId);

    return () => shortcutContext.popScope(scopeId);
  }, [shortcutContext, enabled]);

  return scopeIdRef.current;
};

export default useShortcutScope;
